import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getAdminDashboard } from "@/lib/admin.functions";
import { Users, PlayCircle, Heart, MessageCircle, TrendingUp, UserPlus, Video } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/painel")({
  component: AdminDashboard,
});

function Stat({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: any;
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="mt-2 font-serif text-3xl font-semibold text-primary">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function AdminDashboard() {
  const fn = useServerFn(getAdminDashboard);
  const { data, isLoading } = useQuery({ queryKey: ["admin-dashboard"], queryFn: () => fn() });

  if (isLoading || !data)
    return (
      <div
        className="min-h-[80vh] animate-pulse rounded-xl bg-secondary"
        role="status"
        aria-label="Carregando"
      />
    );
  const c = data.counts;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-serif text-2xl font-semibold text-primary">Painel de engajamento</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Visão consolidada do uso da plataforma pelos membros.
        </p>
      </div>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          icon={Users}
          label="Membros"
          value={c.totalUsers}
          hint={`${c.approvedUsers} com acesso · ${c.pendingUsers} com cadastro incompleto`}
        />
        <Stat icon={Video} label="Ensinos" value={c.totalTeachings} />
        <Stat
          icon={PlayCircle}
          label="Visualizações"
          value={c.totalViews}
          hint={`${c.viewsLast30} nos últimos 30 dias`}
        />
        <Stat icon={UserPlus} label="Novos membros (30d)" value={c.newUsersLast30} />
        <Stat icon={Heart} label="Favoritos" value={c.totalFavorites} />
        <Stat icon={MessageCircle} label="Comentários" value={c.totalComments} />
      </section>

      <section>
        <h2 className="mb-4 flex items-center gap-2 font-serif text-lg font-semibold text-primary">
          <TrendingUp className="h-5 w-5" /> Ensinos mais assistidos
        </h2>
        {data.topTeachings.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Ainda não há visualizações registradas.
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">#</th>
                  <th className="px-4 py-3 text-left">Título</th>
                  <th className="px-4 py-3 text-left">Parashá</th>
                  <th className="px-4 py-3 text-left">Data</th>
                  <th className="px-4 py-3 text-right">Views</th>
                </tr>
              </thead>
              <tbody>
                {data.topTeachings.map((t, i) => (
                  <tr key={t.id} className="border-t border-border">
                    <td className="px-4 py-3 text-muted-foreground">{i + 1}</td>
                    <td className="px-4 py-3">
                      <Link
                        to="/ensinos/$id"
                        params={{ id: t.id }}
                        className="font-medium hover:text-primary"
                      >
                        {t.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{t.parasha ?? "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{t.date ?? "—"}</td>
                    <td className="px-4 py-3 text-right font-semibold text-primary">{t.views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-4 flex items-center gap-2 font-serif text-lg font-semibold text-primary">
          <Users className="h-5 w-5" /> Membros mais ativos
        </h2>
        {data.topUsers.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Sem histórico de visualização ainda.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {data.topUsers.map((u, i) => (
              <div
                key={u.id}
                className="flex items-center justify-between rounded-lg border border-border bg-card p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-primary">
                    {i + 1}
                  </div>
                  <div>
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs text-muted-foreground">Membro engajado</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-serif text-xl font-semibold text-primary">{u.count}</div>
                  <div className="text-xs text-muted-foreground">ensinos</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
